/**
 * Signing an access request with a passkey: asking for a challenge, reading it
 * back, and answering it.
 *
 * A challenge is single use and lives five minutes, so nothing here retries or
 * refetches on its own. The calls are mutations, and the one query turns off
 * retries and focus refetches: a silent second read could show a different
 * status from the one the person is looking at.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useMemberAuthStore } from "modules/members/auth";
import type { AssertionFields, ServerAssertionOptions } from "modules/members/webauthn";
import { memberCall } from "./stepUpApi";
import {
  CHALLENGE_NOT_FOUND_MESSAGE,
  START_SIGNING_FAILED_MESSAGE,
  VERIFY_FAILED_MESSAGE,
  type StepUpFailure,
} from "./stepUpFailure";

export type StepUpPurpose =
  | "request_submission"
  | "approval"
  | "add_passkey"
  | "remove_passkey";

/** `pending` until signed; a pending challenge past its expiry reads `expired`. */
export type StepUpStatus = "pending" | "consumed" | "expired" | "cancelled";

export type StepUpParty = { memberId: string; email: string } | null;

/**
 * The canonical payload the passkey signs (X.StepUp.Payload). Shown to the
 * person before the prompt: without it the signature proves presence, not
 * intent. Every field is optional here because the page must still render
 * something honest for a payload version it does not know.
 */
export type StepUpPayload = {
  v?: number;
  type?: string;
  orgId?: string;
  requestId?: string;
  requester?: StepUpParty;
  subject?: StepUpParty;
  entitlement?: {
    catalogItemId?: string | null;
    selectableResourceId?: string | null;
    app?: string | null;
    role?: string | null;
    identityId?: string | null;
  } | null;
  duration?: { indefinite?: boolean; seconds?: number | null } | null;
  justification?: string | null;
  approvalStep?: { index?: number | null; name?: string | null } | null;
  policyVersion?: string;
  nonce?: string;
  expiresAt?: string;
};

/** GET /members/step-up/challenges/:id (XWeb.Members.StepUpView). */
export type StepUpChallenge = {
  id: string;
  purpose: StepUpPurpose;
  status: StepUpStatus;
  code: string;
  requestId: string | null;
  approvalStage: number | null;
  expiresAt: string;
  payload: StepUpPayload;
  publicKey: ServerAssertionOptions;
  hasPasskeys: boolean;
};

/**
 * One passkey signature on an access request, as the member request views
 * render it. Absent from servers that predate signed approvals.
 */
export type MFSignature = {
  role: "requester" | "approver" | "admin_override" | string;
  /** Zero-based stage index for an approver's signature; null for the requester. */
  approvalStage: number | null;
  signedAt: string;
  signerName: string | null;
  /**
   * Set when the signer reported the passkey as not theirs ("wasn't me"): the
   * signature stands as evidence but an admin should review it. Absent on
   * servers that predate the report.
   */
  flaggedForReviewAt?: string | null;
};

/** What verify returns: the request, as AccessRequestView renders it. */
export type SignedRequest = { id: string; status: string };

/**
 * A request whose policy wants its requester's signature is created as a
 * draft in this status, and goes for approval only once signed.
 */
export const AWAITING_SIGNATURE = "awaiting_signature";

export const needsSignature = (
  request: { status?: string | null } | null | undefined,
): boolean => request?.status === AWAITING_SIGNATURE;

/**
 * The drafts in a batch of create results: single creates (`{id, status}`)
 * and bulk rows (`{ok, requestId, status}`) both work. A row without a status
 * (a server that predates drafts) is never a draft.
 */
export const draftIdsToSign = (
  results: Array<
    | { id?: string; requestId?: string; ok?: boolean; status?: string | null }
    | null
    | undefined
  >,
): string[] =>
  results.flatMap((r) => {
    if (!r || r.ok === false || !needsSignature(r)) return [];
    const id = r.requestId ?? r.id;
    return id ? [id] : [];
  });


const challengePath = (id: string) =>
  `members/step-up/challenges/${encodeURIComponent(id)}`;

export const fetchChallenge = (id: string): Promise<StepUpChallenge> =>
  memberCall<StepUpChallenge>("GET", challengePath(id), undefined, CHALLENGE_NOT_FOUND_MESSAGE);

/** Mints a challenge for the caller to sign their own draft request. */
export const startRequestSignature = (requestId: string): Promise<StepUpChallenge> =>
  memberCall<StepUpChallenge>(
    "POST",
    `members/step-up/requests/${encodeURIComponent(requestId)}/challenge`,
    {},
    START_SIGNING_FAILED_MESSAGE,
  );

/** Mints a challenge for the caller to approve the request's current stage. */
export const startApprovalSignature = (requestId: string): Promise<StepUpChallenge> =>
  memberCall<StepUpChallenge>(
    "POST",
    `members/step-up/approvals/${encodeURIComponent(requestId)}/challenge`,
    {},
    START_SIGNING_FAILED_MESSAGE,
  );

/**
 * The verify body. `action_assignee_id` names the approval assignment the
 * person came from, so the server can retire that assignment's chat buttons;
 * `comments` is an approver's note, recorded with the decision as an inline
 * approval's would be. Each is left out when there is none.
 */
export const verifyBody = (
  assertion: AssertionFields,
  actionAssigneeId?: string | null,
  comments?: string | null,
) => {
  const note = comments?.trim();
  return {
    credential_id: assertion.credentialId,
    authenticator_data: assertion.authenticatorData,
    signature: assertion.signature,
    client_data_json: assertion.clientDataJson,
    ...(actionAssigneeId ? { action_assignee_id: actionAssigneeId } : {}),
    ...(note ? { comments: note } : {}),
  };
};

export const verifyChallenge = (
  id: string,
  assertion: AssertionFields,
  actionAssigneeId?: string | null,
  comments?: string | null,
): Promise<SignedRequest> =>
  memberCall<SignedRequest>(
    "POST",
    `${challengePath(id)}/verify`,
    verifyBody(assertion, actionAssigneeId, comments),
    VERIFY_FAILED_MESSAGE,
  );

export const challengeKey = (id: string) => ["mf", "step-up", "challenge", id];

export const useStepUpChallenge = (id: string | undefined) => {
  // Opened from a link, the page mounts while the portal is still refreshing
  // its token. The read waits for that token, like every member query
  // (`memberCall` would also join the refresh in flight, so this is for
  // consistency with them rather than a guard against that race).
  const accessToken = useMemberAuthStore((s) => s.accessToken);
  return useQuery<StepUpChallenge, StepUpFailure>(
    challengeKey(id ?? ""),
    () => fetchChallenge(id as string),
    {
      enabled: Boolean(id && accessToken),
      // This page handles its own loading and errors; the global suspense
      // and error-boundary defaults would blank it on a 404.
      suspense: false,
      useErrorBoundary: false,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    },
  );
};

const NO_RETRY = { retry: false as const };

export const useStartRequestSignature = () =>
  useMutation<StepUpChallenge, StepUpFailure, string>(
    (requestId) => startRequestSignature(requestId),
    NO_RETRY,
  );

export const useStartApprovalSignature = () =>
  useMutation<StepUpChallenge, StepUpFailure, string>(
    (requestId) => startApprovalSignature(requestId),
    NO_RETRY,
  );

/**
 * Re-reads the challenge into the page's cache. Used after enrolment (the new
 * passkey must join `allowCredentialIds`) and after a failed verify (to learn
 * whether the challenge expired or was used).
 */
export const useRefetchChallenge = () => {
  const queryClient = useQueryClient();
  return async (id: string): Promise<StepUpChallenge> => {
    const fresh = await fetchChallenge(id);
    queryClient.setQueryData(challengeKey(id), fresh);
    return fresh;
  };
};

/**
 * Everything a signature changes: whatever the inline create and approve
 * paths invalidate (see `useCreateAccessRequest` and the decide hooks in
 * access_v2/catalogs.ts), plus the task feed and its counts, since a signed
 * approval completes the approver's task, and the passkeys list, whose "last
 * used" just moved.
 */
export const STEP_UP_INVALIDATIONS = [
  ["mf", "access", "active_requests"],
  ["mf", "access", "current_access"],
  ["mf", "access", "my_active_requests"],
  ["mf", "access", "managed-access"],
  ["mf", "access", "request"],
  ["mf", "approval", "request"],
  ["mf", "assignments"],
  ["mf", "tasksFeed"],
  ["mf", "me", "stats"],
  ["mf", "passkeys"],
];

/**
 * Refreshes everything a signature changed (STEP_UP_INVALIDATIONS), and marks
 * the signed challenge consumed wherever it is cached. Its read never
 * refetches, so without this the signing page, mounted on it again (Back
 * after "Go to My Access"), offered a second signature the server refuses.
 */
export const refreshAfterSignature = (
  queryClient: QueryClient,
  challengeId: string,
) => {
  queryClient.setQueryData<StepUpChallenge>(
    challengeKey(challengeId),
    (cached) => cached && { ...cached, status: "consumed" },
  );
  STEP_UP_INVALIDATIONS.forEach((queryKey) =>
    queryClient.invalidateQueries({ queryKey }),
  );
};
